import { useEffect, useRef, useState } from 'react';
import type { GeoJSONSource, Map as MapLibreMap, MapMouseEvent, StyleSpecification } from 'maplibre-gl';
import type { LatLng } from '@popcart/core/v1';
import { tokens } from '@popcart/tokens';
// The shared style lives in @popcart/ui, whose index pulls React Native; import the style file alone.
import { popcartTrackingStyle, styleFromApi } from '../../../../../packages/ui/src/map/style';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from '../zones/lib.js';
import { addKitIcons } from '../../ui/mapIcons';
import { latLngLabel, roundLatLng } from './lib.js';
import 'maplibre-gl/dist/maplibre-gl.css';

const c = tokens.colour;

function pinGeoJSON(p: LatLng | null) {
  return { type: 'FeatureCollection', features: p ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } }] : [] };
}

/**
 * The pickup pin on the PopCart map (MapLibre web, the shared style, like Zones & fees): click the
 * map to drop or move it. A Pop Yellow pin with an Ink edge. Typed coordinates are the keyboard
 * path and the fallback when the map can't load.
 */
export function PinPicker({ id, value, onChange, error }: { id: string; value: LatLng | null; onChange(p: LatLng): void; error?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const live = useRef(onChange);
  live.current = onChange;
  const [lat, setLat] = useState(value ? String(value.lat) : '');
  const [lng, setLng] = useState(value ? String(value.lng) : '');

  // Keep the typed fields in step with a map click (or a loaded shop).
  useEffect(() => {
    setLat(value ? String(value.lat) : '');
    setLng(value ? String(value.lng) : '');
  }, [value?.lat, value?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    let map: MapLibreMap | null = null;
    void Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')])
      .then(([{ Map, NavigationControl, setWorkerUrl }, { default: workerUrl }]) => {
        if (cancelled || !box.current) return;
        setWorkerUrl(workerUrl);
        const start = value ? ([value.lng, value.lat] as [number, number]) : DEFAULT_CENTER;
        map = new Map({ container: box.current, style: styleFromApi(popcartTrackingStyle, window.location.origin) as unknown as StyleSpecification, center: start, zoom: value ? 16 : DEFAULT_ZOOM, attributionControl: { compact: true } });
        addKitIcons(map);
        map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
        map.on('load', () => {
          if (!map) return;
          map.addSource('pin', { type: 'geojson', data: pinGeoJSON(null) as never });
          map.addLayer({ id: 'pin-dot', type: 'circle', source: 'pin', paint: { 'circle-radius': 10, 'circle-color': c.core.volt, 'circle-stroke-color': c.core.ink, 'circle-stroke-width': 3 } });
          setReady(true);
        });
        map.on('click', (e: MapMouseEvent) => live.current(roundLatLng({ lat: e.lngLat.lat, lng: e.lngLat.lng })));
        map.on('error', () => undefined);
        mapRef.current = map;
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource('pin') as GeoJSONSource | undefined)?.setData(pinGeoJSON(value) as never);
    map.getCanvas().style.cursor = 'crosshair';
  }, [ready, value]);

  const typed = (nextLat: string, nextLng: string) => {
    setLat(nextLat);
    setLng(nextLng);
    const a = Number(nextLat);
    const b = Number(nextLng);
    if (nextLat.trim() && nextLng.trim() && Number.isFinite(a) && Number.isFinite(b)) {
      const p = roundLatLng({ lat: a, lng: b });
      onChange(p);
      mapRef.current?.flyTo({ center: [p.lng, p.lat], zoom: 16 });
    }
  };

  const errId = `${id}-err`;
  return (
    <div className="field">
      <span className="flabel" id={`${id}-label`}>
        Pickup pin
      </span>
      <div className="zn-map-wrap">
        <div ref={box} className="zn-map sh-pin-map" role="application" aria-labelledby={`${id}-label`} aria-describedby={`${id}-help`} />
        {failed ? (
          <p className="zn-map-msg pill st-warning err-pill" role="status" style={{ whiteSpace: 'normal' }}>
            The map didn't load. Type the coordinates below.
          </p>
        ) : !ready ? (
          <p className="zn-map-msg t-monocap muted" role="status">
            Loading the map…
          </p>
        ) : null}
      </div>
      <span id={`${id}-help`} className="help">
        Click the map where riders pick up. Pin: <span className="t-mono">{latLngLabel(value)}</span>
      </span>
      <div className="row wrap sh-coords">
        <div className="field grow">
          <label htmlFor={`${id}-lat`}>Latitude</label>
          <input id={`${id}-lat`} className="input" inputMode="decimal" autoComplete="off" value={lat} aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined} onChange={(e) => typed(e.target.value, lng)} />
        </div>
        <div className="field grow">
          <label htmlFor={`${id}-lng`}>Longitude</label>
          <input id={`${id}-lng`} className="input" inputMode="decimal" autoComplete="off" value={lng} aria-invalid={error ? true : undefined} aria-describedby={error ? errId : undefined} onChange={(e) => typed(lat, e.target.value)} />
        </div>
      </div>
      {error ? (
        <span id={errId} className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
